import { useState, type FormEvent } from "react";
import { asDecimalString } from "@shop-ledger/shared-types";
import { Button, DataTable, Input, type DataTableColumn } from "@shop-ledger/ui-components";
import { useAuth } from "../../auth/AuthContext";
import { createExchangeRate, listExchangeRates } from "../../api/exchangeRatesApi";
import type { ExchangeRateOut } from "../../api/types";
import { StatusBanner } from "../../components/StatusBanner";
import { ApiError } from "../../lib/apiClient";
import { useAsyncData } from "../../lib/useAsyncData";
import { ROLES_WITH_TAX_FX_WRITE } from "../../roles/permissions";

const COLUMNS: DataTableColumn<ExchangeRateOut, keyof ExchangeRateOut>[] = [
  { key: "from_currency", header: "From", render: (r) => r.from_currency },
  { key: "to_currency", header: "To", render: (r) => r.to_currency },
  { key: "rate", header: "Rate", align: "right", render: (r) => r.rate },
  { key: "effective_at", header: "Effective at", render: (r) => new Date(r.effective_at).toLocaleString() },
];

/**
 * Manual exchange rates — `GET/POST /exchange-rates` (§13.2). No PATCH exists on the
 * backend: a correction is a new row with a later `effective_at`, which
 * `ManualExchangeRateProvider` picks up automatically as the latest applicable rate for a
 * currency pair at transaction time (frozen onto `Sale.exchange_rate_to_tenant_currency`).
 */
export function ExchangeRatesPage() {
  const { user, ensureFreshToken } = useAuth();
  const canWrite = user ? ROLES_WITH_TAX_FX_WRITE.includes(user.role) : false;
  const ratesState = useAsyncData(() => ensureFreshToken().then((token) => listExchangeRates(token)), []);

  const [fromCurrency, setFromCurrency] = useState("");
  const [toCurrency, setToCurrency] = useState("");
  const [rate, setRate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  async function handleCreate(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      const token = await ensureFreshToken();
      await createExchangeRate(token, {
        from_currency: fromCurrency.toUpperCase(),
        to_currency: toCurrency.toUpperCase(),
        rate: asDecimalString(rate),
      });
      setFromCurrency("");
      setToCurrency("");
      setRate("");
      ratesState.reload();
    } catch (error) {
      setFormError(error instanceof ApiError ? error.message : "Could not create exchange rate.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Exchange Rates</h1>
          <p className="page__subtitle">
            Manual FX rates — a sale in a foreign currency freezes the applicable rate at transaction time
            onto the sale itself, so historical sales are never affected by a later correction here.
          </p>
        </div>
      </div>

      {ratesState.error ? <StatusBanner kind="error">{ratesState.error}</StatusBanner> : null}
      {!canWrite && user ? (
        <StatusBanner kind="info">
          Your role ({user.role}) has read access to exchange rates. Adding new rates is restricted to
          OWNER/ACCOUNTANT.
        </StatusBanner>
      ) : null}

      {canWrite ? (
        <div className="panel">
          <h2 className="panel__title">New exchange rate</h2>
          <form onSubmit={handleCreate}>
            <div className="form-grid">
              <Input label="From currency" value={fromCurrency} onChange={(e) => setFromCurrency(e.target.value)} placeholder="e.g. USD" maxLength={3} required />
              <Input label="To currency" value={toCurrency} onChange={(e) => setToCurrency(e.target.value)} placeholder="e.g. UGX" maxLength={3} required />
              <Input label="Rate" inputMode="decimal" value={rate} onChange={(e) => setRate(e.target.value)} placeholder="e.g. 3800.00" required />
            </div>
            {formError ? <StatusBanner kind="error">{formError}</StatusBanner> : null}
            <div className="form-actions">
              <Button type="submit" disabled={submitting}>
                {submitting ? "Creating…" : "Add rate"}
              </Button>
            </div>
          </form>
        </div>
      ) : null}

      <div className="panel">
        {ratesState.loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <DataTable columns={COLUMNS} rows={ratesState.data ?? []} getRowId={(row) => row.id} emptyMessage="No exchange rates recorded." caption="Exchange Rates" />
        )}
      </div>
    </div>
  );
}
